import { resolveApiBaseUrl } from './apiBaseUrl';
import { apiClient } from './client';

export interface BaseWellCurvesProgress {
  progress: number;
  message: string;
  stage: string;
}

async function streamBaseWellCurves(
  scenarioLayerId: string,
  onProgress?: (progress: BaseWellCurvesProgress) => void,
): Promise<BaseWellCurvesResponse> {
  const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token') || '';
  const response = await fetch(
    `${resolveApiBaseUrl()}/scenario-layers/${scenarioLayerId}/base-well-curves/stream`,
    {
      method: 'GET',
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        Accept: 'text/event-stream',
      },
      credentials: 'include',
    },
  );

  if (!response.ok) {
    let detail = `HTTP error! status: ${response.status}`;
    try {
      const body = await response.json();
      if (body?.detail) {
        detail = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail);
      }
    } catch {
      // ignore parse errors
    }
    throw new Error(detail);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('Response body is not readable');
  }

  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;

      const data = JSON.parse(line.slice(6));
      if (data.type === 'error') {
        throw new Error(data.message || 'Failed to load base well curves');
      }
      if (data.type === 'result') {
        return data.data as BaseWellCurvesResponse;
      }
      if (data.type === 'heartbeat' || data.progress !== undefined) {
        onProgress?.({
          progress: typeof data.progress === 'number' ? data.progress : 0,
          message: data.message || 'Loading base well curves...',
          stage: data.stage || 'processing',
        });
      }
    }
  }

  throw new Error('Stream ended without result');
}

export interface ScenarioLayerResource {
  id: string;
  scenario_layer_id: string;
  initial_extractable?: number | null;
  initial_geological?: number | null;
}

export interface VolumetricPointPayload {
  gross_thickness_m?: number | null;
  net_to_gross_pct?: number | null;
  porosity_pct?: number | null;
  water_saturation_pct?: number | null;
  oil_fvf?: number | null;
}

export interface VolumetricPoint extends VolumetricPointPayload {
  column_index: number;
  geological_thousand_tons?: number | null;
}

export interface VolumetricEstimatePayload {
  area_km2?: number | null;
  oil_density_g_cm3?: number | null;
  points: VolumetricPointPayload[];
}

export interface VolumetricEstimateUpsert extends VolumetricEstimatePayload {
  total_geological_thousand_tons?: number | null;
}

export interface VolumetricCalculateResponse {
  area_km2: number;
  oil_density_g_cm3: number;
  active_point_count: number;
  area_per_point_km2: number;
  total_geological_thousand_tons: number;
  points: VolumetricPoint[];
}

export interface VolumetricEstimate {
  id: string;
  scenario_layer_id: string;
  area_km2?: number | null;
  oil_density_g_cm3?: number | null;
  total_geological_thousand_tons?: number | null;
  points: VolumetricPoint[];
}

export type AnalogueAggregation = 'mean' | 'weighted_mean' | 'p10' | 'p50' | 'p90' | 'manual';

export type AnalogueChartKey = 'oil' | 'liquid' | 'water_cut_pct' | 'recovery_factor';

export interface AnalogueChartAggregations {
  oil: AnalogueAggregation;
  liquid: AnalogueAggregation;
  water_cut_pct: AnalogueAggregation;
  recovery_factor: AnalogueAggregation;
}

export interface AnalogueManualOilSettings {
  enabled: boolean;
  q0: number | null;
  a: number | null;
}

export interface AnalogueManualLiquidSettings {
  enabled: boolean;
  q0: number | null;
  a: number | null;
  wc0: number | null;
  k: number | null;
}

export interface AnalogueManualWcSettings {
  enabled: boolean;
  wc0: number | null;
  k: number | null;
}

export interface AnalogueManualRecoverySettings {
  enabled: boolean;
  value: number | null;
}

export interface AnalogueManualSettings {
  oil: AnalogueManualOilSettings;
  liquid: AnalogueManualLiquidSettings;
  water_cut_pct: AnalogueManualWcSettings;
  recovery_factor: AnalogueManualRecoverySettings;
}

export interface AnalogueChartSettingsUpdate {
  aggregations?: Partial<AnalogueChartAggregations>;
  manual?: Partial<{
    oil: Partial<AnalogueManualOilSettings>;
    liquid: Partial<AnalogueManualLiquidSettings>;
    water_cut_pct: Partial<AnalogueManualWcSettings>;
    recovery_factor: Partial<AnalogueManualRecoverySettings>;
  }>;
}

export interface ScenarioLayer {
  scenario_layer_id: string;
  scenario_id: string;
  layer_name: string;
  ref_field?: string | null;
  ref_layer?: string | null;
  ref_layer_template?: string | null;
  analogue_aggregation?: AnalogueAggregation | null;
  analogue_count?: number;
  recovery_factor?: number | null;
  properties: ScenarioLayerProperty[];
  resource?: ScenarioLayerResource | null;
  field?: {
    id: string;
    name: string;
    type: string;
  };
  layer?: {
    id: string;
    name: string;
    description?: string;
    type?: string;
    field_id?: string;
  };
  layer_template?: {
    id: string;
    name: string;
    type?: string;
    is_public: boolean;
  };
}

export interface ScenarioLayerProperty {
  id: string;
  scenario_layer_id: string;
  string_value?: string;
  float_value?: number;
  integer_value?: number;
  attribute_id: string;
  attribute?: {
    id: string;
    name: string;
    unit?: string;
    category?: string;
    data_type?: string;
  };
}

export interface ScenarioLayerResourceCreate {
  initial_extractable?: number | null;
  initial_geological?: number | null;
}

export interface ScenarioLayerCreate {
  scenario_id: string;
  layer_name: string;
  ref_field?: string | null;
  ref_layer?: string | null;
  ref_layer_template?: string | null;
  properties: ScenarioLayerPropertyCreate[];
  resource?: ScenarioLayerResourceCreate | null;
}

export interface ScenarioLayerPropertyCreate {
  string_value?: string;
  float_value?: number;
  integer_value?: number;
  attribute_id: string;
}

export const scenarioLayersApi = {
  // Get layers for a scenario
  getScenarioLayers: async (scenarioId: string): Promise<ScenarioLayer[]> => {
    console.log('API: Getting scenario layers for scenarioId:', scenarioId);
    const response = await apiClient.get(`/scenarios/${scenarioId}/layers`);
    console.log('API: Raw response:', response);
    console.log('API: Response type:', typeof response);
    console.log('API: Response is array:', Array.isArray(response));
    return response;
  },

  // Create new scenario layer
  createScenarioLayer: async (scenarioId: string, layerData: ScenarioLayerCreate): Promise<ScenarioLayer> => {
    console.log('API: Creating scenario layer:', layerData);
    const response = await apiClient.post(`/scenarios/${scenarioId}/layers`, layerData);
    console.log('API: Create response:', response);
    return response;
  },

  // Update scenario layer
  updateScenarioLayer: async (scenarioLayerId: string, layerData: Partial<ScenarioLayerCreate>): Promise<ScenarioLayer> => {
    const response = await apiClient.put(`/scenario-layers/${scenarioLayerId}`, layerData);
    return response.data;
  },

  // Delete scenario layer
  deleteScenarioLayer: async (scenarioLayerId: string): Promise<void> => {
    await apiClient.delete(`/scenario-layers/${scenarioLayerId}`);
  },

  /** Single-well decline curves for convolution-based layer forecasts (SSE with heartbeat during ML). */
  getBaseWellCurves: async (
    scenarioLayerId: string,
    onProgress?: (progress: BaseWellCurvesProgress) => void,
  ): Promise<BaseWellCurvesResponse> => {
    return streamBaseWellCurves(scenarioLayerId, onProgress);
  },

  /** Nearest DB layers by petrophysics + decline/liquid/WC/recovery factor for validation UI. */
  validateThroughDatabase: async (
    scenarioLayerId: string,
  ): Promise<ValidateThroughDatabaseResponse> => {
    return apiClient.get(
      `/scenario-layers/${scenarioLayerId}/validate-through-database`,
      { timeout: 120_000 },
    );
  },

  searchAnaloguesByName: async (
    query: string,
    excludeLayerIds: string[] = [],
  ): Promise<ScenarioLayerAnalogueHit[]> => {
    const params = new URLSearchParams();
    params.set('q', query);
    excludeLayerIds.forEach((id) => params.append('exclude_layer_id', id));
    const response = await apiClient.get<{ items: ScenarioLayerAnalogueHit[] }>(
      `/scenario-analogues/search-by-name?${params.toString()}`,
    );
    return response.items || [];
  },

  listAnalogueFields: async (
    search = '',
    skip = 0,
    limit = 25,
  ): Promise<{
    fields: Array<{ id: string; name: string; coordinate?: string | null }>;
    total: number;
  }> => {
    const params = new URLSearchParams();
    if (search.trim()) params.set('search', search.trim());
    params.set('skip', String(skip));
    params.set('limit', String(limit));
    return apiClient.get(`/scenario-analogues/fields?${params.toString()}`);
  },

  listAnalogueMapFields: async (): Promise<{
    fields: Array<{ id: string; name: string; coordinate: string | null }>;
    total: number;
  }> => apiClient.get('/scenario-analogues/fields/map'),

  listAnalogueFieldLayers: async (
    fieldId: string,
  ): Promise<{
    field_id: string;
    field_name: string;
    layers: Array<{
      layer_id: string;
      layer_name: string;
      type: string | null;
      has_type_well_curve: boolean;
    }>;
  }> => {
    return apiClient.get(`/scenario-analogues/fields/${fieldId}/layers`);
  },

  searchAnaloguesByProperties: async (
    scenarioLayerId: string,
    filters: Array<{ attribute_id: string; min_value: number | null; max_value: number | null }>,
    excludeLayerIds: string[] = [],
  ): Promise<ScenarioLayerAnalogueHit[]> => {
    const response = await apiClient.post<{ items: ScenarioLayerAnalogueHit[] }>(
      '/scenario-analogues/search-by-properties',
      {
        scenario_layer_id: scenarioLayerId,
        filters,
        exclude_layer_ids: excludeLayerIds,
      },
    );
    return response.items || [];
  },

  getAnalogues: async (scenarioLayerId: string): Promise<ScenarioLayerAnaloguesPayload> => {
    return apiClient.get(`/scenario-layers/${scenarioLayerId}/analogues`);
  },

  replaceAnalogues: async (
    scenarioLayerId: string,
    body: { analogue_layer_ids: string[]; analogue_aggregation?: AnalogueAggregation | null },
  ): Promise<ScenarioLayerAnaloguesPayload> => {
    return apiClient.put(`/scenario-layers/${scenarioLayerId}/analogues`, body);
  },

  setAnalogueAggregation: async (
    scenarioLayerId: string,
    analogueAggregation: AnalogueAggregation,
  ): Promise<ScenarioLayerAnaloguesPayload> => {
    return apiClient.put(`/scenario-layers/${scenarioLayerId}/analogue-aggregation`, {
      analogue_aggregation: analogueAggregation,
    });
  },

  getAnaloguePropertyMaxima: async (): Promise<Record<string, number>> => {
    return apiClient.get('/scenario-analogues/property-maxima');
  },

  setAnalogueChartSettings: async (
    scenarioLayerId: string,
    body: AnalogueChartSettingsUpdate,
  ): Promise<ScenarioLayerAnaloguesPayload> => {
    return apiClient.put(`/scenario-layers/${scenarioLayerId}/analogue-chart-settings`, body);
  },

  getVolumetricEstimate: async (scenarioLayerId: string): Promise<VolumetricEstimate | null> => {
    try {
      return await apiClient.get(`/scenario-layers/${scenarioLayerId}/volumetric-estimate`);
    } catch (e: unknown) {
      const status = (e as { status_code?: number })?.status_code;
      if (status === 404) return null;
      throw e;
    }
  },

  saveVolumetricEstimate: async (
    scenarioLayerId: string,
    body: VolumetricEstimateUpsert,
  ): Promise<VolumetricEstimate> => {
    return apiClient.put(`/scenario-layers/${scenarioLayerId}/volumetric-estimate`, body);
  },

  calculateVolumetricEstimate: async (
    scenarioLayerId: string,
    body: VolumetricEstimatePayload,
  ): Promise<VolumetricCalculateResponse> => {
    return apiClient.post(`/scenario-layers/${scenarioLayerId}/volumetric-estimate/calculate`, body);
  },

  applyVolumetricEstimate: async (
    scenarioLayerId: string,
    body: VolumetricEstimateUpsert,
  ): Promise<ScenarioLayer> => {
    const response = await apiClient.post(
      `/scenario-layers/${scenarioLayerId}/volumetric-estimate/apply`,
      body,
    );
    return response.data ?? response;
  },
};

export interface BaseWellCurvesResponse {
  scenario_layer_id: string;
  scenario_id?: string;
  layer_name?: string;
  oil_rate: number[];
  liquid_rate: number[];
  water_cut_pct: number[];
  /** `database` = served from `scenario_layer_base_curve`; `computed` = ML run and rows upserted */
  curves_source?: 'database' | 'computed' | 'custom_ml';
  feature_warnings?: {
    extra_attribute_ids?: string[];
    missing_high_coverage_attribute_ids?: string[];
    messages?: string[];
  } | null;
}

export interface ValidateThroughDatabaseCurves {
  oil: number[];
  liquid: number[];
  water_cut_pct: number[];
}

export interface ValidateThroughDatabaseColumn {
  id: string;
  kind: 'target' | 'analogue';
  layer_id: string | null;
  layer_name: string;
  field_name: string | null;
  is_fallback: boolean;
  properties_disclosed: boolean;
  similarity_score: number;
  median_relative_deviation: number;
  recovery_factor: number | null;
  properties: Record<string, number> | null;
  curves: ValidateThroughDatabaseCurves;
}

export interface ValidateThroughDatabaseResponse {
  scenario_layer_id: string;
  layer_name: string;
  used_fallback_pool: boolean;
  n_years: number;
  attribute_rows: Array<{ id: string; name: string; unit?: string | null }>;
  target: ValidateThroughDatabaseColumn;
  analogues: ValidateThroughDatabaseColumn[];
}

export interface ScenarioLayerAnalogueHit {
  id?: string;
  layer_id: string;
  layer_name: string;
  field_id: string;
  field_name: string;
  recovery_factor: number | null;
  similarity_score?: number | null;
  properties?: Record<string, number>;
  curves: ValidateThroughDatabaseCurves;
  q0?: number;
  a?: number;
  wc0?: number | null;
  k?: number | null;
}

export interface ScenarioLayerAnaloguesPayload {
  scenario_layer_id: string;
  layer_name: string;
  analogue_aggregation: AnalogueAggregation | null;
  aggregations?: AnalogueChartAggregations;
  manual?: AnalogueManualSettings;
  recovery_factor: number | null;
  target_properties: Record<string, number>;
  attribute_rows: Array<{ id: string; name: string; unit?: string | null }>;
  analogues: ScenarioLayerAnalogueHit[];
  aggregated?: {
    methods: AnalogueChartAggregations;
    oil: number[];
    liquid: number[];
    water_cut_pct: number[];
    recovery_factor: number | null;
  } | null;
}
